import type { SSEMessage } from '@/_shared/types';

/**
 * Find the user_message content associated with the given turnId.
 *
 * Two patterns to handle:
 * 1. Dashboard sidebar / optimistic: user_message sits BEFORE turn_start
 *    (addMessage fires before SSE delivers turn_start).
 * 2. Regular SSE replay: user_message sits AFTER turn_start.
 *
 * Algorithm: find the turn_start index, then check immediately before it
 * (pattern 1), then scan forward after it (pattern 2).
 */
export function findUserMessageInTurn(messages: SSEMessage[], turnId: string): string | null {
  const startIdx = messages.findIndex((m) => m.type === 'turn_start' && m.turnId === turnId);
  if (startIdx === -1) return null;

  // Pattern 1: user_message immediately before turn_start (optimistic addMessage)
  const before = messages[startIdx - 1];
  if (before?.type === 'user_message') {
    return before.content;
  }

  // Pattern 2: user_message after turn_start (SSE-delivered order)
  for (let i = startIdx + 1; i < messages.length; i++) {
    const m = messages[i];
    if (!m) continue;
    if (m.type === 'turn_start') break; // crossed into next turn
    if (m.type === 'user_message') return m.content;
  }

  return null;
}
